/**
 * EventRequirementsPanel — embedded tab panel for Lead and Opportunity pages
 * Shows structured event requirements, allows create/edit/lock/change-requests
 */
import { useState, useEffect, useCallback } from "react";
import {
  Loader2, Plus, CheckCircle2, Lock, Unlock, RefreshCw,
  ChevronDown, ChevronUp, AlertCircle,
} from "lucide-react";
import { requirementsApi } from "@/services/api";
import { hasCapability } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const C = {
  ivory:   "#FBF7F2",
  sand:    "#FAF6F0",
  gold:    "#C9A15F",
  charcoal:"#3A3330",
  slate:   "#7A706A",
  divider: "rgba(201,161,95,0.15)",
};

const STATUS_CONFIG = {
  DRAFT:       { bg: "#F3F4F6", text: "#374151", label: "Draft" },
  CONFIRMED:   { bg: "#D1FAE5", text: "#065F46", label: "Confirmed" },
  SCOPE_LOCKED:{ bg: "#DBEAFE", text: "#1E40AF", label: "Scope Locked" },
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : null;

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;
  return (
    <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={{ background: cfg.bg, color: cfg.text }}>
      {cfg.label}
    </span>
  );
}

// ── Create/Edit Requirements Form ─────────────────────────────────────────────
function RequirementsForm({ existing, leadId, opportunityId, eventMasterId, onClose, onSuccess }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    event_title: "",
    event_type: "",
    event_date: "",
    guest_min: "",
    guest_max: "",
    venue_preference: "",
    venue_location: "",
    style_preference: "",
    budget_min_inr: "",
    budget_max_inr: "",
    constraints: "",
    preferences: "",
    additional_notes: "",
    ...(existing?.customer_data || {}),
    guest_min: existing?.customer_data?.guest_min || "",
    guest_max: existing?.customer_data?.guest_max || "",
    budget_min_inr: existing?.customer_data?.budget_min_inr || "",
    budget_max_inr: existing?.customer_data?.budget_max_inr || "",
  });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      const customer_data = {
        ...form,
        guest_min: form.guest_min ? parseInt(form.guest_min) : null,
        guest_max: form.guest_max ? parseInt(form.guest_max) : null,
        budget_min_inr: form.budget_min_inr ? parseFloat(form.budget_min_inr) : null,
        budget_max_inr: form.budget_max_inr ? parseFloat(form.budget_max_inr) : null,
      };
      if (existing) {
        await requirementsApi.update(existing.id, { customer_data });
        toast.success("Requirements updated");
      } else {
        await requirementsApi.create({
          lead_id: leadId,
          opportunity_id: opportunityId,
          event_master_id: eventMasterId,
          source: opportunityId ? "CRM_MANUAL" : "CRM_MANUAL",
          customer_data,
        });
        toast.success("Requirements created");
      }
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const Field = ({ label, field, type = "text", placeholder = "" }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        data-testid={`req-field-${field}`}
        type={type}
        className="mt-1 h-8 text-sm"
        placeholder={placeholder}
        value={form[field] || ""}
        onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))}
      />
    </div>
  );

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{existing ? "Edit Event Requirements" : "Add Event Requirements"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Event Title" field="event_title" placeholder="e.g. Priya's Wedding" />
            <Field label="Event Type" field="event_type" placeholder="e.g. Wedding, Corporate" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Event Date" field="event_date" type="date" />
            <Field label="Venue Location" field="venue_location" placeholder="City / Venue" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Min Guests" field="guest_min" type="number" placeholder="0" />
            <Field label="Max Guests" field="guest_max" type="number" placeholder="0" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Min Budget (INR)" field="budget_min_inr" type="number" placeholder="0" />
            <Field label="Max Budget (INR)" field="budget_max_inr" type="number" placeholder="0" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Venue Preference" field="venue_preference" placeholder="Indoor, Outdoor, Hotel..." />
            <Field label="Style Preference" field="style_preference" placeholder="Traditional, Fusion..." />
          </div>
          <div>
            <Label className="text-xs">Constraints / Special Requirements</Label>
            <textarea
              data-testid="req-field-constraints"
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm resize-none"
              rows={2}
              placeholder="Any constraints or special requirements..."
              value={form.constraints || ""}
              onChange={e => setForm(p => ({ ...p, constraints: e.target.value }))}
            />
          </div>
          <div>
            <Label className="text-xs">Additional Notes</Label>
            <textarea
              data-testid="req-field-notes"
              className="mt-1 w-full border rounded-md px-3 py-2 text-sm resize-none"
              rows={2}
              placeholder="Any other notes..."
              value={form.additional_notes || ""}
              onChange={e => setForm(p => ({ ...p, additional_notes: e.target.value }))}
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              data-testid="save-requirements-btn"
              className="flex-1"
              style={{ background: C.gold }}
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {existing ? "Save Changes" : "Create Requirements"}
            </Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────
export default function EventRequirementsPanel({ leadId, opportunityId, eventMasterId }) {
  const { user } = useAuth();
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState({});

  const canManage = hasCapability(user, "requirements.manage");
  const canLock   = hasCapability(user, "requirements.lock");

  const fetchRequirements = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (leadId)         params.lead_id = leadId;
      if (opportunityId)  params.opportunity_id = opportunityId;
      if (eventMasterId)  params.event_master_id = eventMasterId;
      const { data } = await requirementsApi.list(params);
      setRequirements(data);
    } catch {
      toast.error("Failed to load requirements");
    } finally {
      setLoading(false);
    }
  }, [leadId, opportunityId, eventMasterId]);

  useEffect(() => { fetchRequirements(); }, [fetchRequirements]);

  const handleTransition = async (rid, target) => {
    try {
      await requirementsApi.transition(rid, { target_status: target });
      toast.success(`Requirements ${target === "CONFIRMED" ? "confirmed" : target === "SCOPE_LOCKED" ? "locked" : "updated"}`);
      fetchRequirements();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to transition");
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-24" style={{ color: C.slate }}>
      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading requirements...
    </div>
  );

  return (
    <div data-testid="event-requirements-panel" className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium" style={{ color: C.charcoal }}>
          Event Requirements {requirements.length > 0 && `(${requirements.length})`}
        </div>
        <div className="flex gap-2">
          <button onClick={fetchRequirements} className="p-1 rounded hover:bg-black/5">
            <RefreshCw className="w-3.5 h-3.5" style={{ color: C.slate }} />
          </button>
          {canManage && (
            <Button
              data-testid="add-requirements-btn"
              size="sm"
              style={{ background: C.gold, color: "#fff" }}
              onClick={() => { setEditing(null); setShowForm(true); }}
              className="h-7 text-xs px-3"
            >
              <Plus className="w-3 h-3 mr-1" /> Add Requirements
            </Button>
          )}
        </div>
      </div>

      {requirements.length === 0 ? (
        <div className="text-sm text-center py-8" style={{ color: C.slate }}>
          No structured requirements yet.
          {canManage && (
            <button
              className="block mx-auto mt-2 text-xs underline"
              style={{ color: C.gold }}
              onClick={() => setShowForm(true)}
            >
              Add event requirements
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {requirements.map(req => {
            const isExpanded = expanded[req.id];
            const cd = req.customer_data || {};
            const vd = req.planner_verified_data;
            const vs = req.validation_status || {};
            return (
              <div
                key={req.id}
                data-testid={`requirements-card-${req.id}`}
                className="rounded-xl overflow-hidden"
                style={{ background: C.sand, border: `1px solid ${C.divider}` }}
              >
                {/* Card header */}
                <div
                  className="p-4 flex items-center justify-between gap-3 cursor-pointer"
                  onClick={() => setExpanded(e => ({ ...e, [req.id]: !e[req.id] }))}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium" style={{ color: C.charcoal }}>
                      {cd.event_title || "Event Requirements"}
                    </span>
                    <StatusBadge status={req.status} />
                    <span className="text-xs" style={{ color: C.slate }}>v{req.version}</span>
                    {vs.completeness_pct != null && (
                      <span className="text-xs px-1.5 py-0.5 rounded"
                        style={{
                          background: vs.completeness_pct >= 80 ? "#D1FAE5" : "#FEF3C7",
                          color: vs.completeness_pct >= 80 ? "#065F46" : "#92400E"
                        }}
                      >
                        {vs.completeness_pct}% complete
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {/* Transition actions */}
                    {canManage && req.status === "DRAFT" && (
                      <Button
                        data-testid={`confirm-req-btn-${req.id}`}
                        size="sm"
                        className="h-6 text-xs px-2"
                        style={{ background: "#065F46", color: "#fff" }}
                        onClick={e => { e.stopPropagation(); handleTransition(req.id, "CONFIRMED"); }}
                      >
                        Confirm
                      </Button>
                    )}
                    {canLock && req.status === "CONFIRMED" && (
                      <Button
                        data-testid={`lock-req-btn-${req.id}`}
                        size="sm"
                        className="h-6 text-xs px-2"
                        style={{ background: "#1E40AF", color: "#fff" }}
                        onClick={e => { e.stopPropagation(); handleTransition(req.id, "SCOPE_LOCKED"); }}
                      >
                        <Lock className="w-3 h-3 mr-1" /> Lock Scope
                      </Button>
                    )}
                    {canManage && req.status !== "SCOPE_LOCKED" && (
                      <button
                        data-testid={`edit-req-btn-${req.id}`}
                        className="p-1 rounded hover:bg-black/5"
                        onClick={e => { e.stopPropagation(); setEditing(req); setShowForm(true); }}
                      >
                        <span className="text-xs" style={{ color: C.slate }}>Edit</span>
                      </button>
                    )}
                    {isExpanded ? <ChevronUp className="w-4 h-4" style={{ color: C.slate }} /> : <ChevronDown className="w-4 h-4" style={{ color: C.slate }} />}
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="border-t px-4 pb-4 pt-3 space-y-3" style={{ borderColor: C.divider }}>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {[
                        { label: "Event Date",   value: fmtDate(cd.event_date) || cd.event_date },
                        { label: "Guest Range",  value: (cd.guest_min || cd.guest_max) ? `${cd.guest_min || "?"} – ${cd.guest_max || "?"}` : null },
                        { label: "Budget (INR)", value: (cd.budget_min_inr || cd.budget_max_inr) ? `₹${Number(cd.budget_min_inr || 0).toLocaleString()} – ₹${Number(cd.budget_max_inr || 0).toLocaleString()}` : null },
                        { label: "Venue",        value: cd.venue_preference || cd.venue_location },
                        { label: "Style",        value: cd.style_preference },
                        { label: "Event Type",   value: cd.event_type },
                      ].filter(f => f.value).map(f => (
                        <div key={f.label} className="rounded p-2" style={{ background: C.ivory }}>
                          <div className="text-xs" style={{ color: C.slate }}>{f.label}</div>
                          <div className="text-sm font-medium mt-0.5" style={{ color: C.charcoal }}>{f.value}</div>
                        </div>
                      ))}
                    </div>
                    {cd.constraints && (
                      <div className="text-xs" style={{ color: C.slate }}>
                        <span className="font-medium" style={{ color: C.charcoal }}>Constraints: </span>
                        {cd.constraints}
                      </div>
                    )}
                    {cd.additional_notes && (
                      <div className="text-xs" style={{ color: C.slate }}>
                        <span className="font-medium" style={{ color: C.charcoal }}>Notes: </span>
                        {cd.additional_notes}
                      </div>
                    )}
                    {vd && (
                      <div className="rounded p-3 mt-2" style={{ background: "#D1FAE5", border: "1px solid #6EE7B7" }}>
                        <div className="text-xs font-medium text-green-800 mb-1">Planner Verified</div>
                        <div className="text-xs text-green-700">Verified by {vd.verified_by} · {fmtDate(vd.verified_at)}</div>
                        {vd.verification_notes && <div className="text-xs text-green-700 mt-1">{vd.verification_notes}</div>}
                      </div>
                    )}
                    {/* Change requests */}
                    {(req.change_requests || []).length > 0 && (
                      <div className="mt-2">
                        <div className="text-xs font-medium mb-1.5" style={{ color: C.charcoal }}>Change Requests ({req.change_requests.length})</div>
                        {req.change_requests.map(cr => (
                          <div key={cr.change_request_id} className="rounded p-2 mb-1 text-xs"
                            style={{ background: C.ivory, border: `1px solid ${C.divider}` }}>
                            <div className="flex items-center justify-between">
                              <span style={{ color: C.charcoal }}>{cr.description}</span>
                              <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                cr.status === "APPROVED" ? "bg-green-100 text-green-800" :
                                cr.status === "REJECTED" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"
                              }`}>{cr.status}</span>
                            </div>
                            <div className="mt-0.5" style={{ color: C.slate }}>
                              Requested by {cr.requested_by_name} · {fmtDate(cr.requested_at)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <RequirementsForm
          existing={editing}
          leadId={leadId}
          opportunityId={opportunityId}
          eventMasterId={eventMasterId}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSuccess={fetchRequirements}
        />
      )}
    </div>
  );
}
